import { trackEvent } from '@/components/Analytics';
import ShareToXButton from '@/components/ShareToXButton';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import LearnMeta, { countWordsInElement } from '@/components/LearnMeta';
import StockModal from '@/components/StockModal';
import { fetchStockWithFallback } from '@/lib/stockFetch';
import { useAuth } from '@/contexts/AuthContext';
import { LearnStockModalContext } from './learnStockModalContext';
import LearnSidebar from './LearnSidebar';
import LearnGuideFeedback from '@/components/LearnGuideFeedback';

/**
 * useSeoMeta — Round 91m (Feb 2026).
 *
 * Sets per-page <title> and <meta name="description"> on mount and
 * restores the defaults on unmount. Modern Googlebot reads
 * client-rendered titles + descriptions, so this is enough for
 * per-page SEO without server-side rendering.
 *
 * Also bumps the og:title / og:description / twitter:title /
 * twitter:description for proper unfurls when these pages are
 * shared on Reddit, X, Discord.
 */
export function useSeoMeta({ title, description, canonical }) {
  useEffect(() => {
    const prevTitle = document.title;
    const ensureMeta = (selector, attr, key, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', value);
      return () => {
        if (prev != null) el.setAttribute('content', prev);
        else el.removeAttribute('content');
      };
    };
    const ensureLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      let created = false;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
        created = true;
      }
      const prev = el.getAttribute('href');
      el.setAttribute('href', href);
      return () => {
        if (created) el.remove();
        else if (prev != null) el.setAttribute('href', prev);
      };
    };

    if (title) document.title = title;
    const restorers = [];
    if (description) {
      restorers.push(ensureMeta('meta[name="description"]', 'name', 'description', description));
      restorers.push(ensureMeta('meta[property="og:description"]', 'property', 'og:description', description));
      restorers.push(ensureMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description));
    }
    if (title) {
      restorers.push(ensureMeta('meta[property="og:title"]', 'property', 'og:title', title));
      restorers.push(ensureMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title));
    }
    if (canonical) {
      restorers.push(ensureLink('canonical', canonical));
      restorers.push(ensureMeta('meta[property="og:url"]', 'property', 'og:url', canonical));
    }
    return () => {
      document.title = prevTitle;
      restorers.forEach((r) => r && r());
    };
  }, [title, description, canonical]);
}

/**
 * LearnPage — shared chrome for /learn/* SEO pages.
 *
 * Renders Back/Home button, a clear H1, the page content, and a
 * Related-pages footer so each Learn page builds an internal-link
 * graph (Googlebot rewards internal linking).
 */
export default function LearnPage({
  title,
  description,
  canonical,
  h1,
  kicker,
  children,
  related = [],
  jsonLd,
  titleAdornment = null,
  sectionLabel,
  sectionAnchor,
  datePublished,
  wide = false,
  guidePath = null,
  hideBackButton = false,
  share = null,
  matchHeader = false,
}) {
  useSeoMeta({ title, description, canonical });
  const gaReadRef = useRef(null);
  useEffect(() => {
    const path = guidePath || window.location.pathname;
    if (!path.startsWith('/learn/') || gaReadRef.current === path) return;
    gaReadRef.current = path;
    trackEvent('learn_guide_read', { guide_path: path, guide_title: h1 || title });
  }, [guidePath, h1, title]);

  // Round 96p (Jul 4 2026) — <SeeItLive> chips inside a guide need
  // to open the Stock Modal over the current Learn page (no nav,
  // no refresh). We host the modal at the LearnPage layer and
  // expose an openLearnStock() function through context.
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [modalInitialTab, setModalInitialTab] = useState('dividend-info');
  const [loadingStock, setLoadingStock] = useState(false);
  const openLearnStock = useCallback(async (ticker, tab) => {
    if (!ticker) return;
    setModalInitialTab(tab || 'dividend-info');
    setLoadingStock(true);
    setModalOpen(true);
    try {
      const data = await fetchStockWithFallback(ticker, {
        timeoutMs: 3500,
        onFresh: (fresh) => setSelectedStock(fresh),
      });
      setSelectedStock(data);
    } catch {
      setSelectedStock(null);
    } finally {
      setLoadingStock(false);
    }
  }, []);
  const closeLearnStock = useCallback(() => {
    setModalOpen(false);
    setSelectedStock(null);
    // Prevent the sessionStorage-based auto-reopen from firing on
    // the next navigation — the user intentionally dismissed.
    try { sessionStorage.removeItem('dividrip:modal-state'); } catch { /* ignore */ }
  }, []);
  // Sep 2026 — in-modal ticker links (nesting-doll pills, peer tables,
  // issuer rows) swap the modal to the new ticker; StockModal keeps the
  // breadcrumb trail itself via navigateToTicker → onSelectStock.
  const selectLearnStock = useCallback(async (ticker) => {
    if (!ticker) return;
    setLoadingStock(true);
    try {
      const data = await fetchStockWithFallback(ticker, {
        timeoutMs: 3500,
        onFresh: (fresh) => setSelectedStock(fresh),
      });
      setSelectedStock(data);
    } catch {
      setSelectedStock(null);
    } finally {
      setLoadingStock(false);
    }
  }, []);
  // Any in-modal <Link> (e.g. "Learn: CLO ETFs" pointing at THIS guide)
  // pushes a new history entry; close the overlay so the reader lands
  // on the article instead of staring at the still-open modal.
  const location = useLocation();
  const lastLocKey = useRef(location.key);
  useEffect(() => {
    if (lastLocKey.current !== location.key) {
      lastLocKey.current = location.key;
      if (modalOpen) closeLearnStock();
    }
  }, [location.key, modalOpen, closeLearnStock]);

  // Measure rendered article word count once on mount so the reading-
  // time pill can show "X min read" without each guide having to count
  // its own words.
  const articleRef = useRef(null);
  const [wordCount, setWordCount] = useState(null);
  useEffect(() => {
    if (articleRef.current) {
      setWordCount(countWordsInElement(articleRef.current));
    }
  }, [children]);

  useEffect(() => {
    if (!jsonLd) return undefined;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    script.setAttribute('data-learn-jsonld', '1');
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [jsonLd]);

  // Round 95y (Jun 2026) — BlogPosting JSON-LD for every Learn guide.
  // Enables Google's "Top Stories" / "About this result" / article-card
  // SERP enrichment, distinct from the FAQPage schema each guide already
  // ships. Author is the site brand ("Twylightcrow"); datePublished is
  // optional per-guide (older guides without a known write date emit
  // BlogPosting with only dateModified, which Google accepts).
  useEffect(() => {
    if (!canonical || !h1) return undefined;
    const blogPosting = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: h1,
      description: description || undefined,
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
      author: { '@type': 'Organization', name: 'Twylightcrow', url: 'https://twylightcrow.com/' },
      publisher: {
        '@type': 'Organization',
        name: 'DiviDrip',
        logo: { '@type': 'ImageObject', url: 'https://twylightcrow.com/dividrip-icon.png?v=3' },
      },
      // Today's date as the dateModified — accurate enough since the
      // template auto-updates dollar/yield references on every build.
      dateModified: new Date().toISOString().slice(0, 10),
    };
    if (datePublished) {
      blogPosting.datePublished = datePublished;
    }
    if (sectionLabel) {
      blogPosting.articleSection = sectionLabel;
    }
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(blogPosting);
    script.setAttribute('data-learn-blogposting', '1');
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [canonical, h1, description, datePublished, sectionLabel]);

  // Round 92f — BreadcrumbList structured data so Googlebot can
  // render the trail in SERPs. Mounted only when a sectionLabel is
  // provided (LearnIndex itself doesn't need a breadcrumb).
  useEffect(() => {
    if (!sectionLabel || !canonical) return undefined;
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://twylightcrow.com/' },
        { '@type': 'ListItem', position: 2, name: 'Learn', item: 'https://twylightcrow.com/learn' },
        {
          '@type': 'ListItem',
          position: 3,
          name: sectionLabel,
          item: sectionAnchor
            ? `https://twylightcrow.com/learn#${sectionAnchor}`
            : 'https://twylightcrow.com/learn',
        },
        { '@type': 'ListItem', position: 4, name: h1, item: canonical },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(breadcrumb);
    script.setAttribute('data-learn-breadcrumb', '1');
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [sectionLabel, sectionAnchor, h1, canonical]);

  // Round 96v (Jul 4 2026) — Guide pages opt into the persistent
  // left-column section-nav by passing `guidePath`. When set, we
  // auto-widen the container so both the sidebar and the article
  // fit comfortably on lg+, and render the sidebar as a sticky
  // column next to the article.
  const useWideLayout = wide || Boolean(guidePath);

  return (
    <LearnStockModalContext.Provider value={{ openLearnStock }}>
    <div className="min-h-screen min-h-[100svh] bg-background text-foreground">
      <div className={`${matchHeader ? 'max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-8 pb-0' : `${useWideLayout ? 'max-w-6xl' : 'max-w-3xl'} mx-auto px-4 sm:px-6 pt-8 pb-0`}`}>
        {!hideBackButton && <BackOrHomeButton testid="learn-back-or-home-btn" />}
        {kicker && (
          <p className="mt-6 text-xs tracking-[0.35em] uppercase text-violet-500/85 dark:text-violet-300/70 font-semibold">
            {kicker}
          </p>
        )}
        {share && (
          <div className="mt-3" data-testid="learn-share-row">
            <ShareToXButton variant="button" url={share.url} text={share.text} hashtags={share.hashtags || []} ticker={share.key || 'learn'} label={share.label || 'Share this guide'} />
          </div>
        )}
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight bg-gradient-to-br from-violet-600 via-violet-500 to-amber-600 bg-clip-text text-transparent">
          {h1}
          {titleAdornment ? (
            <span className="ml-3 inline-block align-middle text-base font-normal not-italic [-webkit-text-fill-color:initial] [background:none] [color:initial]">
              {titleAdornment}
            </span>
          ) : null}
        </h1>
        {sectionLabel && (
          <LearnMeta
            sectionLabel={sectionLabel}
            sectionAnchor={sectionAnchor}
            wordCount={wordCount}
          />
        )}
        {guidePath ? (
          // Guide-page layout: sticky sidebar (lg+) + article column.
          <div className="lg:flex lg:gap-8 lg:items-start mt-6">
            <aside
              aria-label="Learn guide navigation"
              className="hidden lg:block lg:sticky lg:top-24 lg:self-start lg:w-56 lg:shrink-0 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto pr-2 hide-scrollbar"
              data-testid="learn-guide-sidebar"
            >
              <LearnSidebar mode="guide" currentGuidePath={guidePath} />
            </aside>
            <article ref={articleRef} className="learn-article prose prose-violet dark:prose-invert max-w-none lg:flex-1 lg:min-w-0">
              {children}
            </article>
          </div>
        ) : (
          <article ref={articleRef} className="learn-article mt-6 prose prose-violet dark:prose-invert max-w-none">
            {children}
          </article>
        )}

        {/* Round P108ap — Was-this-guide-helpful thumbs strip. Rendered
            for every /learn/* guide that carries a `guidePath` so admins
            can review the responses via the new Learn-Guide Feedback
            button next to How-To Feedback. Placed BEFORE the
            Related-guides divider per user spec so the strip sits at
            the natural end of the article body. */}
        {guidePath && <LearnGuideFeedback guidePath={guidePath} />}

        {related.length > 0 && (
          <section className="mt-12 pt-6 border-t border-border" data-testid="learn-related">
            <h2 className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3 font-semibold">
              Related guides
            </h2>
            <ul className="space-y-2">
              {related.map((r) => (
                <li key={r.to}>
                  <Link
                    to={r.to}
                    className="inline-flex items-baseline gap-2 text-sm text-violet-700 dark:text-violet-300 hover:text-amber-600 dark:hover:text-amber-400 hover:underline"
                  >
                    <span>→</span>
                    <span>{r.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <p className="mt-10 text-xs text-muted-foreground italic leading-relaxed">
          DiviDrip is a free dividend portfolio tracker built by dividend investors,
          for dividend investors. <Link to="/dashboard" className="underline hover:text-foreground">Try it free</Link> —
          no payment, no upsell, ever. Read the <Link to="/how-to" className="underline hover:text-foreground">How-To</Link>{' '}
          to see how each tool fits together, or browse the <Link to="/glossary" className="underline hover:text-foreground">Glossary</Link> for
          every term defined.
        </p>
      </div>

      {/* Round 91y — site-wide footer (disclaimer + nav links + brand).
          Same footer used on Dashboard, Glossary, Privacy, WhatIf etc.
          Also inherited by every /stock/:ticker Snapshot page since
          they wrap themselves in <LearnPage />. */}
      <SiteDisclaimer />

      {/* Round 96p — Stock Modal overlay for in-guide "See it live"
          chips. Rendered as a sibling to the article so closing the
          modal leaves the reader at their exact scroll position. */}
      <StockModal
        stock={selectedStock}
        open={modalOpen}
        onClose={closeLearnStock}
        loading={loadingStock}
        isDark={typeof document !== 'undefined' && document.documentElement.classList.contains('dark')}
        user={user}
        onLoginRequired={() => navigate('/?login=1')}
        initialTab={modalInitialTab}
        onSelectStock={selectLearnStock}
      />
    </div>
    </LearnStockModalContext.Provider>
  );
}
